import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Settings, Save } from 'lucide-react';
import { API, useAuthHeader } from './hooks';

const categoryLabels = {
  homepage: 'Homepage',
  about: 'About / Artist Page',
  gallery: 'Gallery Page',
  contact: 'Contact Page',
  studio_info: 'Studio Info',
  social: 'Social Links',
  first_timer: 'First Timer Zone',
  healing_tracker: 'Healing Tracker',
  quote_estimator: 'Quote Estimator',
  gift_cards: 'Gift Cards',
  footer: 'Footer',
};

const ContentManager = () => {
  const getAuthHeader = useAuthHeader();
  const [items, setItems] = useState([]);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchContent = async () => {
    try {
      const res = await axios.get(`${API}/content`);
      const list = res.data.items || [];
      setItems(list);
      const map = {};
      list.forEach((it) => { map[it.key] = it.value; });
      setEdits(map);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchContent(); }, []);

  const seedContent = async () => {
    try {
      const res = await axios.post(`${API}/content/seed`, {}, getAuthHeader());
      toast.success(res.data.message);
      fetchContent();
    } catch (err) {
      toast.error('Failed to seed content');
    }
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const updates = Object.entries(edits).map(([key, value]) => ({ key, value: String(value ?? '') }));
      await axios.post(`${API}/content/bulk-update`, { updates }, getAuthHeader());
      toast.success('Content saved successfully!');
      fetchContent();
    } catch (err) {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const grouped = items.reduce((acc, item) => {
    const cat = item.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  return (
    <div data-testid="content-management">
      <div className="flex justify-between items-center mb-8 flex-wrap gap-3">
        <h2 className="font-heading text-3xl flex items-center gap-3">
          <Settings size={28} /> Site Content
        </h2>
        <div className="flex gap-2">
          {items.length === 0 && (
            <button
              onClick={seedContent}
              data-testid="seed-content-btn"
              className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-blue-700"
            >
              Seed Default Content
            </button>
          )}
          <button
            onClick={saveAll}
            disabled={saving || items.length === 0}
            data-testid="save-content-btn"
            className="flex items-center gap-2 bg-green-600 text-white px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-green-700 disabled:opacity-50"
          >
            <Save size={16} />
            {saving ? 'Saving...' : 'Save All Changes'}
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-gray-200 p-8 text-center">
          <p className="text-gray-500 mb-4">No site content yet. Click "Seed Default Content" to populate.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([category, catItems]) => (
            <div key={category} className="bg-white border border-gray-200 p-6">
              <h3 className="font-heading text-xl mb-6 pb-2 border-b border-gray-200">
                {categoryLabels[category] || category}
              </h3>
              <div className="grid gap-6">
                {catItems.map((item) => (
                  <div key={item.key} className="flex flex-col md:flex-row md:items-start gap-4">
                    <div className="md:w-1/4">
                      <label className="font-mono text-xs uppercase tracking-wider text-gray-500">
                        {item.label}
                      </label>
                    </div>
                    <div className="md:w-3/4">
                      {item.content_type === 'textarea' ? (
                        <textarea
                          value={edits[item.key] ?? ''}
                          onChange={(e) => setEdits((p) => ({ ...p, [item.key]: e.target.value }))}
                          rows={3}
                          data-testid={`content-input-${item.key}`}
                          className="w-full border-2 border-gray-300 px-4 py-3 font-body text-sm focus:border-black focus:outline-none resize-none"
                        />
                      ) : (
                        <input
                          type={item.content_type === 'number' ? 'number' : 'text'}
                          value={edits[item.key] ?? ''}
                          onChange={(e) => setEdits((p) => ({ ...p, [item.key]: e.target.value }))}
                          data-testid={`content-input-${item.key}`}
                          className="w-full border-2 border-gray-300 px-4 py-3 font-body text-sm focus:border-black focus:outline-none"
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContentManager;
